/**
 * Feature flags service - fetches public feature flags from the API.
 * Caches result to avoid repeated requests.
 */

export interface FeatureFlags {
  juryReports: boolean;
  /** OAuth 2.0 authorization-code + PKCE login (FEATURE_AUTH_CODE_FLOW) */
  authCodeFlow: boolean;
  /** ENG-1759: text-first ticket creation with category suggestions (flag on + model loaded) */
  categorySuggestions: boolean;
  /** ENG-1759: simplified player report flow (FEATURE_SIMPLE_REPORT_FLOW) */
  simpleReportFlow: boolean;
  /** ENG-1759: flattened support ticket flow (FEATURE_SIMPLE_TICKET_FLOW) */
  simpleTicketFlow: boolean;
  /** FEATURE_REPORT_QUEUE: staff report queue page + nav link */
  reportQueue: boolean;
  /** allowed evidence link prefixes (same list the server checks); empty = unknown, skip client validation */
  attachmentLinks: string[];
}

let cachedFlags: FeatureFlags | null = null;

/**
 * Get feature flags. Caches result - call invalidateCache() to force refresh.
 * Returns default (all false) on error (fail-closed).
 */
export async function getFeatureFlags(): Promise<FeatureFlags> {
  if (cachedFlags !== null) {
    return cachedFlags;
  }

  try {
    const axios = (await import('axios')).default;
    const response = await axios.get('/features');
    // Axios interceptor unwraps to data; TypeScript sees AxiosResponse
    const data = (response as unknown) as { features?: { juryReports?: boolean; authCodeFlow?: boolean; categorySuggestions?: boolean; simpleReportFlow?: boolean; simpleTicketFlow?: boolean; reportQueue?: boolean; attachmentLinks?: string[] } };
    cachedFlags = {
      juryReports: data?.features?.juryReports === true,
      authCodeFlow: data?.features?.authCodeFlow === true,
      categorySuggestions: data?.features?.categorySuggestions === true,
      simpleReportFlow: data?.features?.simpleReportFlow === true,
      simpleTicketFlow: data?.features?.simpleTicketFlow === true,
      reportQueue: data?.features?.reportQueue === true,
      attachmentLinks: Array.isArray(data?.features?.attachmentLinks) ? data!.features!.attachmentLinks!.filter(x => typeof x === 'string') : []
    };
    return cachedFlags;
  } catch {
    cachedFlags = { juryReports: false, authCodeFlow: false, categorySuggestions: false, simpleReportFlow: false, simpleTicketFlow: false, reportQueue: false, attachmentLinks: [] };
    return cachedFlags;
  }
}

/**
 * Invalidate the cache (e.g. after admin changes config)
 */
export function invalidateFeatureFlagsCache(): void {
  cachedFlags = null;
}
